import { computed, ref, useHost, onMounted, toRef, type Ref } from "vue";
import type { TextareaProps } from "./textarea.types";
import { useSharedState } from "composables/useSharedState";
import { useFieldInheritance } from "components/Field/useField";
import { createEventDispatcher } from "@/runtime/shared/events";
import { useFormAssociated } from "composables/useFormAssociated";
import { textareaClasses } from "./textarea.styles";
import { cardClasses } from "components/Card/card.styles";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";

export const useTextarea = (props: TextareaProps) => {
  const host = useHost();
  const state = useSharedState(props);
  const dispatchEvent = createEventDispatcher(host);
  const { finalColor, finalVariant, finalSize } = useFieldInheritance(host, state);
  const textareaRef = ref<HTMLTextAreaElement | null>(null);

  const { validate } = useFormAssociated({
    inputRef: textareaRef as unknown as Ref<HTMLInputElement | null>,
    modelValue: state.modelValue as Ref<string | null | undefined>,
    disabled: toRef(props, "disabled"),
    required: toRef(props, "required"),
    disableEnterSubmit: true,
  });

  const variantClasses = computed(() =>
    textareaClasses({
      variant: finalVariant.value,
      size: finalSize.value,
      disabled: state.disabled?.value,
      block: state.block?.value,
    }),
  );

  const monoFocusClass = computed(() => {
    if (finalColor.value === "mono" && (finalVariant.value === "solid" || finalVariant.value === "outline")) {
      return "focus-within:ring-0! focus-within:outline-1 focus-within:outline-solid focus-within:outline-(--color-foreground) focus-within:outline-offset-4";
    }
    return "";
  });

  const cardCls = computed(() =>
    cardClasses({ color: finalColor.value, variant: finalVariant.value, size: finalSize.value }),
  );

  const classes = computed(() => ({
    base: mergeClassProp(`${cardCls.value} ${variantClasses.value.base} ${monoFocusClass.value}`.trim(), props.class),
    input: variantClasses.value.input,
  }));

  const currentValue = computed(() => state.modelValue?.value ?? "");

  const handleInput = (e: Event) => dispatchEvent("update:modelValue", (e.target as HTMLTextAreaElement).value);
  const handleChange = () => {
    validate();
    dispatchEvent("change", true);
  };
  const handleBlur = () => {
    validate();
    dispatchEvent("blur", true);
  };

  if (host) (host as HTMLElement).focus = () => textareaRef.value?.focus();
  onMounted(() => {
    if (props.autofocus) setTimeout(() => textareaRef.value?.focus(), props.autofocusDelay ?? 0);
  });

  return { classes, textareaRef, currentValue, handleInput, handleChange, handleBlur };
};
